import React, { useCallback, useEffect, useState } from 'react';
import { apiCall } from '../../utils/api';
import { toast } from '../ui/sonner';
import {
  Trophy, Loader2, Wallet, Sparkles, X, Save, RefreshCw, ShieldCheck,
} from 'lucide-react';

const TIER_STYLE = {
  silver: 'bg-slate-100 text-slate-700 border-slate-300',
  gold: 'bg-amber-100 text-amber-800 border-amber-300',
};
const STATUS_STYLE = {
  pending: 'bg-blue-50 text-blue-700 border-blue-200',
  released: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  rejected: 'bg-rose-50 text-rose-600 border-rose-200',
};

function ReleaseModal({ reward, settings, onClose, onDone, isAdmin }) {
  const tierDefaults = reward.org_defaults || settings?.[reward.tier] || (reward.tier === 'gold' ? { wallet_amount: 25, spins: 3 } : { wallet_amount: 10, spins: 1 });
  const [rewardType, setRewardType] = useState('wallet');
  const [amount, setAmount] = useState(tierDefaults.wallet_amount);
  const [spins, setSpins] = useState(tierDefaults.spins);
  const [busy, setBusy] = useState(false);

  const release = async () => {
    setBusy(true);
    try {
      const body = rewardType === 'wallet'
        ? { reward_type: 'wallet', amount: parseFloat(amount) }
        : { reward_type: 'spins', spins: parseInt(spins, 10) };
      await apiCall(`/api/org/streak-rewards/${reward._id}/release`, {
        method: 'POST', body: JSON.stringify(body),
      });
      toast.success(`Reward released to ${reward.worker_name}`);
      onDone();
      onClose();
    } catch (e) {
      toast.error('Release failed', { description: e.message });
    }
    setBusy(false);
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden" onClick={(e) => e.stopPropagation()} data-testid="release-reward-modal">
        <div className="bg-gradient-to-r from-amber-500 to-orange-500 p-4 flex items-center justify-between text-white">
          <div className="flex items-center gap-2">
            <Trophy className="w-5 h-5" />
            <div>
              <h3 className="font-bold">Release {reward.tier === 'gold' ? 'Gold' : 'Silver'} Streak Reward</h3>
              <p className="text-[11px] text-white/85">{reward.worker_name} · {reward.streak_at_detection} on-time shifts</p>
            </div>
          </div>
          <button onClick={onClose} className="p-1.5 hover:bg-white/15 rounded-lg" data-testid="close-release-modal"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-4 space-y-4">
          <div className="grid grid-cols-2 gap-2">
            <button
              onClick={() => setRewardType('wallet')}
              className={`p-3 rounded-xl border-2 text-left transition ${rewardType === 'wallet' ? 'border-emerald-500 bg-emerald-50' : 'border-slate-200 hover:border-slate-300'}`}
              data-testid="reward-type-wallet"
            >
              <Wallet className="w-4 h-4 text-emerald-600 mb-1" />
              <p className="text-sm font-bold text-slate-900">Wallet bonus</p>
              <p className="text-[11px] text-slate-500">Funded by {isAdmin ? "the org's" : 'your'} wallet</p>
            </button>
            <button
              onClick={() => setRewardType('spins')}
              className={`p-3 rounded-xl border-2 text-left transition ${rewardType === 'spins' ? 'border-violet-500 bg-violet-50' : 'border-slate-200 hover:border-slate-300'}`}
              data-testid="reward-type-spins"
            >
              <Sparkles className="w-4 h-4 text-violet-600 mb-1" />
              <p className="text-sm font-bold text-slate-900">Spin boost</p>
              <p className="text-[11px] text-slate-500">Extra Daily Spin turns</p>
            </button>
          </div>
          {rewardType === 'wallet' ? (
            <div>
              <label className="block text-xs font-bold text-slate-700 uppercase tracking-wide mb-1">Bonus amount ($)</label>
              <input type="number" min="0.5" max="1000" step="0.5" value={amount}
                onChange={(e) => setAmount(e.target.value)}
                className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500"
                data-testid="release-amount-input" />
            </div>
          ) : (
            <div>
              <label className="block text-xs font-bold text-slate-700 uppercase tracking-wide mb-1">Bonus spins (1-10)</label>
              <input type="number" min="1" max="10" step="1" value={spins}
                onChange={(e) => setSpins(e.target.value)}
                className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-violet-500"
                data-testid="release-spins-input" />
            </div>
          )}
        </div>
        <div className="border-t border-slate-100 bg-slate-50 px-4 py-3 flex justify-end gap-2">
          <button onClick={onClose} className="px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-100 rounded-lg">Cancel</button>
          <button onClick={release} disabled={busy}
            className="px-4 py-2 bg-gradient-to-r from-amber-500 to-orange-500 text-white rounded-lg text-sm font-bold inline-flex items-center gap-1.5 disabled:opacity-50"
            data-testid="confirm-release-btn">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trophy className="w-4 h-4" />} Release reward
          </button>
        </div>
      </div>
    </div>
  );
}

function SettingsCard({ settings, onSaved }) {
  const [form, setForm] = useState(settings);
  const [saving, setSaving] = useState(false);
  useEffect(() => { setForm(settings); }, [settings]);
  if (!form) return null;

  const setTier = (tier, key, val) => setForm((p) => ({ ...p, [tier]: { ...p[tier], [key]: val } }));

  const save = async () => {
    setSaving(true);
    try {
      const body = {
        enabled: !!form.enabled,
        silver: { wallet_amount: parseFloat(form.silver.wallet_amount), spins: parseInt(form.silver.spins, 10) },
        gold: { wallet_amount: parseFloat(form.gold.wallet_amount), spins: parseInt(form.gold.spins, 10) },
      };
      const res = await apiCall('/api/org/streak-rewards/settings', { method: 'PUT', body: JSON.stringify(body) });
      toast.success('Streak reward settings saved');
      onSaved(res.settings);
    } catch (e) {
      toast.error('Save failed', { description: e.message });
    }
    setSaving(false);
  };

  return (
    <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-4" data-testid="streak-settings-card">
      <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
        <h3 className="font-bold text-slate-900 flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-indigo-500" /> Reward defaults</h3>
        <label className="flex items-center gap-2 text-sm font-semibold text-slate-700 cursor-pointer">
          <input type="checkbox" checked={!!form.enabled} onChange={(e) => setForm((p) => ({ ...p, enabled: e.target.checked }))}
            className="w-4 h-4 accent-indigo-600" data-testid="streak-enabled-toggle" />
          Detection enabled
        </label>
      </div>
      <div className="grid sm:grid-cols-2 gap-3">
        {['silver', 'gold'].map((tier) => (
          <div key={tier} className={`rounded-xl border p-3 ${TIER_STYLE[tier]}`}>
            <p className="text-xs font-bold uppercase tracking-wide mb-2">{tier} streak {tier === 'gold' ? '(25+ on-time)' : '(10+ on-time)'}</p>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="block text-[10px] font-semibold mb-0.5">Wallet bonus ($)</label>
                <input type="number" min="0" max="1000" step="0.5" value={form[tier].wallet_amount}
                  onChange={(e) => setTier(tier, 'wallet_amount', e.target.value)}
                  className="w-full px-2 py-1.5 border border-slate-300 rounded-lg text-sm bg-white text-slate-900"
                  data-testid={`${tier}-wallet-input`} />
              </div>
              <div>
                <label className="block text-[10px] font-semibold mb-0.5">Bonus spins</label>
                <input type="number" min="0" max="10" step="1" value={form[tier].spins}
                  onChange={(e) => setTier(tier, 'spins', e.target.value)}
                  className="w-full px-2 py-1.5 border border-slate-300 rounded-lg text-sm bg-white text-slate-900"
                  data-testid={`${tier}-spins-input`} />
              </div>
            </div>
          </div>
        ))}
      </div>
      <div className="flex justify-end mt-3">
        <button onClick={save} disabled={saving}
          className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg text-sm font-bold inline-flex items-center gap-1.5 disabled:opacity-50"
          data-testid="save-streak-settings">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save settings
        </button>
      </div>
    </div>
  );
}

export default function StreakRewardsPanel({ role = 'employer' }) {
  const isAdmin = role === 'admin';
  const [rewards, setRewards] = useState([]);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [scanning, setScanning] = useState(false);
  const [releasing, setReleasing] = useState(null);

  const load = useCallback(async (scan = false) => {
    try {
      const url = isAdmin
        ? `/api/admin/streak-rewards${scan ? '?scan=true' : ''}`
        : '/api/org/streak-rewards';
      const res = await apiCall(url);
      setRewards(res.rewards || []);
      if (res.settings) setSettings(res.settings);
      if (res.newly_detected > 0) toast.success(`${res.newly_detected} new streak reward${res.newly_detected !== 1 ? 's' : ''} detected`);
    } catch (e) {
      toast.error('Could not load streak rewards', { description: e.message });
    } finally {
      setLoading(false);
      setScanning(false);
    }
  }, [isAdmin]);

  useEffect(() => { load(); }, [load]);

  const reject = async (r) => {
    try {
      await apiCall(`/api/org/streak-rewards/${r._id}/reject`, { method: 'POST', body: JSON.stringify({ reason: 'Declined by admin' }) });
      toast.success('Reward dismissed');
      load();
    } catch (e) {
      toast.error('Could not dismiss', { description: e.message });
    }
  };

  const pending = rewards.filter((r) => r.status === 'pending');
  const history = rewards.filter((r) => r.status !== 'pending');

  if (loading) {
    return <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-amber-500" /></div>;
  }

  return (
    <div className="space-y-4" data-testid="streak-rewards-panel">
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-4 flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-xl font-bold text-slate-900 flex items-center gap-2">
            <Trophy className="w-5 h-5 text-amber-500" /> Streak Rewards
            {pending.length > 0 && (
              <span className="px-2 py-0.5 bg-amber-500 text-white text-xs rounded-full font-bold" data-testid="pending-rewards-count">{pending.length}</span>
            )}
          </h2>
          <p className="text-sm text-slate-500">
            {isAdmin
              ? 'Release Silver & Gold streak rewards on behalf of employers and agencies (funded by the org wallet)'
              : 'Reward workers who hit Silver (10) and Gold (25) on-time streaks — wallet bonus or spin boost'}
          </p>
        </div>
        {isAdmin && (
          <button onClick={() => { setScanning(true); load(true); }} disabled={scanning}
            className="px-3 py-1.5 rounded-lg text-sm font-bold text-white bg-gradient-to-r from-amber-500 to-orange-500 inline-flex items-center gap-1.5 disabled:opacity-50"
            data-testid="admin-scan-btn">
            {scanning ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />} Scan all orgs
          </button>
        )}
      </div>

      {!isAdmin && settings && <SettingsCard settings={settings} onSaved={setSettings} />}

      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
        <div className="px-4 py-3 border-b border-slate-100 bg-slate-50">
          <h3 className="font-bold text-slate-900 text-sm">Pending releases</h3>
        </div>
        {pending.length === 0 ? (
          <p className="text-sm text-slate-400 text-center py-8" data-testid="no-pending-rewards">No pending streak rewards — they appear automatically when workers hit a tier</p>
        ) : (
          <div className="divide-y divide-slate-100" data-testid="pending-rewards-list">
            {pending.map((r) => (
              <div key={r._id} className="px-4 py-3 flex items-center gap-3 flex-wrap" data-testid={`reward-row-${r._id}`}>
                <span className={`px-2 py-0.5 rounded-full border text-[11px] font-bold uppercase ${TIER_STYLE[r.tier]}`}>{r.tier}</span>
                <div className="flex-1 min-w-[140px]">
                  <p className="text-sm font-semibold text-slate-900">{r.worker_name}</p>
                  <p className="text-[11px] text-slate-500">
                    {r.streak_at_detection} on-time shifts{isAdmin && r.org_name ? ` · ${r.org_name}` : ''} · {new Date(r.created_at).toLocaleDateString()}
                  </p>
                </div>
                <button onClick={() => setReleasing(r)}
                  className="px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-xs font-bold"
                  data-testid={`release-btn-${r._id}`}>Release</button>
                <button onClick={() => reject(r)}
                  className="px-3 py-1.5 bg-slate-100 hover:bg-slate-200 text-slate-600 rounded-lg text-xs font-bold"
                  data-testid={`reject-btn-${r._id}`}>Dismiss</button>
              </div>
            ))}
          </div>
        )}
      </div>

      {history.length > 0 && (
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
          <div className="px-4 py-3 border-b border-slate-100 bg-slate-50">
            <h3 className="font-bold text-slate-900 text-sm">History</h3>
          </div>
          <div className="divide-y divide-slate-100" data-testid="rewards-history">
            {history.slice(0, 30).map((r) => (
              <div key={r._id} className="px-4 py-2.5 flex items-center gap-3 flex-wrap text-sm">
                <span className={`px-2 py-0.5 rounded-full border text-[10px] font-bold uppercase ${TIER_STYLE[r.tier]}`}>{r.tier}</span>
                <span className="font-semibold text-slate-800 flex-1 min-w-[120px]">{r.worker_name}</span>
                <span className="text-xs text-slate-500">
                  {r.status === 'released' && (r.reward_type === 'wallet' ? `$${Number(r.amount).toFixed(2)} bonus` : `${r.spins} spin${r.spins !== 1 ? 's' : ''}`)}
                </span>
                <span className={`px-2 py-0.5 rounded-full border text-[10px] font-bold ${STATUS_STYLE[r.status] || ''}`}>{r.status}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {releasing && (
        <ReleaseModal reward={releasing} settings={settings} isAdmin={isAdmin}
          onClose={() => setReleasing(null)} onDone={() => load()} />
      )}
    </div>
  );
}
